import {
    GridMenuButton,
    GridMenuItem,
} from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import FilterModelPro, {
    GridFilterModelPro,
} from '@/global/components/DataGrid/utils/filterModelPro'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import Menu from '@/global/components/MenuV2'
import { useUrlState } from '@/hooks'
import ContentGuide from 'global/guides/ContentGuide'
import { hasPermission } from 'hooks/useBackendMenu'
import {
    ArrowDownToLineIcon,
    ArrowUpDownIcon,
    ArrowUpToLineIcon,
    KeyboardIcon,
    PencilIcon,
    ScanFaceIcon,
    SettingsIcon,
} from 'lucide-react'
import React from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { Funcionario, funcionario, TabelaHorarios } from 'service/api'
import { PaginatedResponse } from 'service/api/types'
import { useStore } from 'store'
import {
    columnsFiltrosFuncionarios,
    columnsFuncionarios,
} from 'templates/Cadastro/Funcionarios/constants/columns'

export default function Funcionarios() {
    const [{ page, perPage }, setUrlPagination] = useUrlState({
        page: 1,
        perPage: 25,
    })

    const [filterModel, setFilterModel] = React.useState<GridFilterModelPro>({
        items: [
            {
                columnField: 'dtRescisao',
                operatorValue: 'isEmpty',
                value: '',
            },
        ],
        quickFilterValue: '',
    })

    const search = filterModel.quickFilterValue || undefined
    const filter = filterModel.items.length
        ? new FilterModelPro(filterModel).toUrlParams()
        : undefined

    const empresaId = useStore(state => state.companyId)
    const setEmployeeId = useStore(state => state.setEmployeeId)

    const { data, isFetching: isLoading } = funcionario.useFetch<
        PaginatedResponse<
            Funcionario & {
                temAjustesPendentes?: boolean
                TabelaHorario?: TabelaHorarios
            }
        >
    >({
        enabled: true,
        keepPreviousData: true,
        queryParams: {
            empresaId,
            page,
            perPage,
            ...(search && { search }),
            ...(filter && { filter }),
        } as any,
    })

    const columns: GridColDefPro<Funcionario>[] = [
        ...columnsFuncionarios,
        ...columnsFiltrosFuncionarios,
        {
            ...optionsColumn,
            field: '__opcoes',
            type: 'actions',
            getActions: ({ id }) => [
                <Menu.Item href={`${id}/detalhes`} key="detalhes">
                    <Menu.Item.Icon>
                        <SettingsIcon />
                    </Menu.Item.Icon>
                    <Menu.Item.Text>Detalhes</Menu.Item.Text>
                </Menu.Item>,
                hasPermission(['update', 'Funcionários'], () => (
                    <Menu.Item href={`${id}/editar`} key="editar">
                        <Menu.Item.Icon>
                            <PencilIcon />
                        </Menu.Item.Icon>
                        <Menu.Item.Text>Editar</Menu.Item.Text>
                    </Menu.Item>
                )),
                hasPermission(['update', 'Funcionários'], () => (
                    <Menu.Item href={`${id}/cadastro-facial`} key="facial">
                        <Menu.Item.Icon>
                            <ScanFaceIcon />
                        </Menu.Item.Icon>
                        <Menu.Item.Text>Cadastro facial</Menu.Item.Text>
                    </Menu.Item>
                )),
                hasPermission(['read', 'Digitação'], () => (
                    <Menu.Item
                        href="/manutencao-ponto/digitacao"
                        key="digitacao"
                    >
                        <Menu.Item.Icon>
                            <KeyboardIcon />
                        </Menu.Item.Icon>
                        <Menu.Item.Text>Digitação</Menu.Item.Text>
                    </Menu.Item>
                )),
            ],
        },
    ]

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header label={['Cadastros', 'Funcionários']}>
                    <ContentGuide.Filtros
                        SelectEmpresas
                        SelectCentroCusto
                        SelectLocais
                    />
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    serverSide
                    rows={data?.data ?? []}
                    columns={columns}
                    loading={isLoading}
                    totalRows={data?.meta?.total}
                    onPaginationChange={({ pageIndex, pageSize }) => {
                        setUrlPagination({
                            page: pageIndex + 1,
                            perPage: pageSize,
                        })
                    }}
                    onFilterChange={fm => {
                        setFilterModel(fm)
                        setUrlPagination({ page: 1, perPage })
                    }}
                    defaultPageSize={perPage}
                    onAdd
                    ToolbarProps={{
                        showVisibilityButton: true,
                        hideExportButton: true,
                        showQuickFilter: true,
                    }}
                    components={{
                        Toolbar: () => (
                            <GridMenuButton
                                startIcon={<ArrowUpDownIcon />}
                                label="Integração"
                            >
                                {hasPermission([
                                    'read',
                                    'ExportarFuncionários',
                                ]) && (
                                    <GridMenuItem
                                        startIcon={
                                            <ArrowUpToLineIcon className="mr-2" />
                                        }
                                        label="Exportar"
                                        href="exportar"
                                    />
                                )}
                                {hasPermission([
                                    'read',
                                    'ImportarFuncionários',
                                ]) && (
                                    <GridMenuItem
                                        startIcon={
                                            <ArrowDownToLineIcon className="mr-2" />
                                        }
                                        label="Importar"
                                        href="importar"
                                    />
                                )}
                            </GridMenuButton>
                        ),
                    }}
                    onRowClick={value => {
                        setEmployeeId(value.row.id)
                    }}
                />
            </ContentGuide.Card>
            <Outlet
                context={{
                    data: data?.data ?? [],
                }}
            />
        </ContentGuide>
    )
}

export function useFuncionariosOutletContext() {
    return useOutletContext<{
        data?: Array<
            Funcionario & {
                TabelaHorario?: TabelaHorarios
                temAjustesPendentes?: boolean
            }
        >
    }>()
}
